import type { ShotType } from '@glass/types';
// Straight from the wire types, NOT through `~/lib/api`. Screens mock that module, and a
// Jest module mock replaces all of its exports — so re-exporting the enum through it made
// `SHOT_TYPES` undefined inside any suite that mocked the API, at module scope, which
// fails as "cannot read properties of undefined" pointing here rather than at the mock.
import { SHOT_TYPES } from '@glass/types';
import type { GlassArea } from '~/lib/api';
import { isLaminated } from '~/lib/areas';

/**
 * One captured photograph, held in journey state until the submit succeeds.
 *
 * A flat list rather than a map keyed by area: the operator can shoot the same area
 * several times, delete an individual frame, and the ORDER within an area is meaningful
 * (first shot is the straight-on one). A `Record<area, File>` cannot express any of that.
 *
 * Nothing is uploaded before submit. A dropped connection then costs a retry, not a
 * re-shoot — and on native the bytes go inline in the submit body anyway, so there is
 * nothing to upload early.
 */
export interface Photo {
  id: string;
  /** Null for the glass-branding shot, which belongs to the job rather than an area. */
  area: GlassArea | null;
  kind: 'area' | 'branding';
  /** Local URI for the thumbnail. */
  uri: string;
  /** Downscaled bytes, ready to submit inline on native. */
  base64?: string;
  bytes: number;
  /**
   * Which of the area's shots this is.
   *
   * The API has modelled this since migration 0014 — `wide | close | angle | scale`, the
   * same four the design draws — accepts it on submit, and answers with `retake_shot`
   * naming which one to re-capture. The app sent none of it and read none of it, so the
   * server could say "retake the scale shot" to a client with no concept of one.
   *
   * Absent only on the branding photo, which belongs to the job rather than an area.
   */
  shot?: ShotType;
  /**
   * Whether an object of known size was in frame.
   *
   * DERIVED from `shot` now, not declared by hand. It used to be a toggle the operator
   * set before pressing the shutter, which put a checkbox between the product's single
   * most consequential input and the person least able to remember it. The scale shot is
   * a named slot; taking it is what sets this.
   *
   * The MODEL also reports it back per area (`scaleReference` in the damage schema), but
   * that arrives after the verdict — too late to prompt a better photograph.
   */
  hasScaleRef: boolean;
}

/**
 * The ceiling the SERVER enforces, mirroring `api.MaxImagesPerArea`.
 *
 * Four, not three. The old constant said 3 while claiming to mirror this, so the client
 * refused a photograph the contract allows and the design asks for.
 */
export const MAX_PHOTOS_PER_AREA = SHOT_TYPES.length;

/**
 * Which shots are worth taking, per kind of glass.
 *
 * **The server does NOT restrict shot types by area.** `POST /v1/jobs` accepts any of
 * `wide | close | angle | scale` on any `glass_area` and caps only the count at 4, so
 * nothing here is about staying inside a validation rule — the phone cannot offer a shot
 * the server rejects, because it rejects none.
 *
 * It is about what the DECISION reads. On a tempered pane `decision.go` looks at
 * `TemperedDamageDetected` alone: any confirmed damage is a replacement, so depth and
 * real-world size change nothing. `angle` exists to judge depth through the laminate and
 * `scale` to get a size rather than a guess, and neither reaches a tempered verdict.
 * Asking for them anyway costs two more analysed images per pane against a 10c journey.
 *
 * `close` stays on every pane: `CloseShotBlind` can turn a tempered `no_damage` into
 * `insufficient_evidence`, so the close shot is load-bearing everywhere.
 */
const LAMINATED_SHOTS: readonly ShotType[] = ['wide', 'close', 'angle', 'scale'];
const TEMPERED_SHOTS: readonly ShotType[] = ['wide', 'close'];

export function shotsFor(area: GlassArea): readonly ShotType[] {
  return isLaminated(area) ? LAMINATED_SHOTS : TEMPERED_SHOTS;
}

/** The word on the pill. */
export const SHOT_LABEL: Record<ShotType, string> = {
  wide: 'Wide',
  close: 'Close',
  angle: 'Angle',
  scale: 'Scale',
};

/**
 * What to do, for the shot being asked for.
 *
 * `close` and `scale` are the design's wording. `angle` keeps the code's, which named the
 * reason ("it is what establishes depth") rather than the act. `wide` is the one line
 * neither supplied and is written here.
 */
export const SHOT_PROMPT: Record<ShotType, string> = {
  wide: 'Fit the whole pane in the frame.',
  close: 'Fill the frame with the damage.',
  angle: 'Shoot from an oblique angle — it is what establishes depth.',
  scale: 'Lay a bank card flat beside the damage, on the glass.',
};

/** Client budget. The submit endpoint caps the whole body at 28 MB. */
export const MAX_TOTAL_BYTES = 24 * 1024 * 1024;

export function photosForArea(photos: Photo[], area: GlassArea): Photo[] {
  return photos.filter((p) => p.kind === 'area' && p.area === area);
}

export function brandingPhoto(photos: Photo[]): Photo | undefined {
  return photos.find((p) => p.kind === 'branding');
}

export function totalBytes(photos: Photo[]): number {
  return photos.reduce((sum, p) => sum + p.bytes, 0);
}

export function canAddToArea(photos: Photo[], area: GlassArea): boolean {
  return photosForArea(photos, area).length < MAX_PHOTOS_PER_AREA;
}

/** Areas with no photograph at all. The server rejects a submit while any remain. */
export function uncoveredAreas(photos: Photo[], areas: GlassArea[]): GlassArea[] {
  return areas.filter((a) => photosForArea(photos, a).length === 0);
}

/** The next shot this area still wants, or null once its set is complete. */
export function nextShotFor(photos: Photo[], area: GlassArea): ShotType | null {
  const taken = shotsTaken(photos, area);
  return shotsFor(area).find((s) => !taken.has(s)) ?? null;
}

/** Which shots this area already has. */
export function shotsTaken(photos: Photo[], area: GlassArea): Set<ShotType> {
  return new Set(
    photosForArea(photos, area)
      .map((p) => p.shot)
      .filter((s): s is ShotType => !!s),
  );
}

/**
 * What to do next in this area, or null once all four are taken.
 *
 * Superseded the count-based hints: the shot has a name now, so the prompt can say what
 * to do rather than guess from how many photographs happen to exist.
 */
export function nextShotHint(photos: Photo[], area: GlassArea): string | null {
  const next = nextShotFor(photos, area);
  return next ? SHOT_PROMPT[next] : null;
}
